import type {StyleVariableCatalog} from '@webflow/variables';
import {ObjectId} from 'bson';
import {
  CodeComponentProp,
  Frameworks,
} from '@packages/systems/code-libraries/propTypes';
import {
  ComponentExport,
  VerifiedLibraryExport,
} from '@packages/systems/libraries';
import {
  CodeModuleRendererErrorCode,
  CodeModuleLoaderType,
} from '@packages/systems/code-modules/core';

export enum Environment {
  Client = 'Client',
  Server = 'Server',
}

export type CodeComponentOptions = {
  applyTagSelectors?: boolean;
  ssr?: boolean;
  prerender?: boolean;
};

export type CodeComponent = {
  /**
   * Component module unique id
   */
  id: string;
  /**
   * Component module url for client-side rendering
   */
  clientModuleUrl: string;
  /**
   * Component module url for server-side rendering
   */
  serverModuleUrl?: string;
  /**
   * Component export name in the module
   */
  exportPath: string;
  /**
   * Component module loader type. Currently we only support FEDERATION modules
   */
  loaderType: CodeModuleLoaderType;
  /**
   * Component framework. Currently we only support React
   */
  framework: Frameworks;
  /**
   * Component display name. If missing - id will be used
   */
  displayName?: string;
  /**
   * Component description
   */
  description?: string;
  /**
   * Component group
   */
  group?: string;
  /**
   * Component hash, used for changelog
   */
  hash?: string;
  /**
   * Component props metadata
   */
  props?: CodeComponentProp[];
  /**
   * Component options
   */
  options?: CodeComponentOptions;
};

export type CodeLibraryManifest = {
  name: string;
  description: string;
  components: CodeComponent[];
  /**
   * ModuleId can be undefined for libraries in the process of being created
   * before a code module is created for them.
   */
  moduleId?: ObjectId | string;
  rendererUrl?: string;
  rendererErrorCode?: CodeModuleRendererErrorCode;
  variableCatalog?: StyleVariableCatalog;
};

/**
 * A verified library export representation of a code component.
 */
export type CodeLibraryExport = Extract<
  VerifiedLibraryExport<string>,
  ComponentExport
>;

export type CodeComponentSourceMap = {
  sources: Record<string, string>;
  dependencies: string[];
};
